/**
 * Relation catalog (schema) for the constraint-rule language.
 *
 * Every question the object model raised — what a bare row coerces to, what an
 * applied reference coerces to, how positional/keyword arguments resolve, which
 * relations are data vs. solver-assigned — is answerable only with a schema.
 * This is it: for each relation, its ordered key columns, its non-key columns,
 * and whether those non-key columns are `data` (parameters) or `decision` (the
 * solver fills them in).
 *
 * The two coercions both read off this schema:
 *   - a bare row variable (`b`, from a parameter `b: battery`) → the relation's key tuple.
 *     A single-key relation unwraps to a scalar; a composite key stays a tuple
 *     that *spreads* across the key positions it indexes (AMPL-style tuple
 *     indexing) — e.g. a `demand` row keyed by (bus, timestep) can index any
 *     relation whose key is (bus, timestep).
 *   - an applied reference (`discharge(n, t)`) → the relation's single non-key
 *     column (its value). Well-defined only when there is exactly one — which is
 *     exactly the shape of a decision variable, and of single-value data tables
 *     like `demand`. A relation with several non-key columns (an entity like
 *     `battery`) is never value-coerced; it's ranged over and read with `.attr`.
 *
 * `keys` names also drive keyword arguments: `demand(bus=n, timestep=t)` matches
 * positional `demand(n, t)` against these names in order.
 *
 * Only the *data* half is written down here. A decision relation is whatever the
 * ranged rule that bounds it says it is — `SOC(b, t)` over the parameters
 * `(b: battery, t: soc_time)` *is* the declaration — so those entries are derived from the rules
 * ({@link catalogFor}) and follow the user's edits.
 *
 * Foreign keys (see {@link foreignKeys}) type the key/reference columns: a column
 * named after a relation is an implicit reference to it, and {@link RelationSchema.refs}
 * spells out the rest (`line.from → bus`). This is what lets a composite-key
 * spread be checked by *referenced relation* rather than only by arity.
 */

import { Rule } from './ruleAst';

export type RelationKind = 'data' | 'decision';

/**
 * What a column holds when it isn't a reference to another relation. `float` is
 * the default — most of this model is megawatts — so only the columns that
 * aren't get written down.
 */
export type ScalarKind = 'int' | 'float' | 'string';

/**
 * A column's declared type, or `unknown` for one nothing has worked out yet.
 *
 * The two are different claims, and the difference matters: an undeclared
 * column is a float *by default*, which is right where the default is a fact
 * about the model, and `unknown` is the absence of an answer — used for a
 * derived relation's value, which is whatever its rule computes and isn't known
 * until the rule has been looked at.
 */
export type ColumnType = ScalarKind | 'unknown';

export interface RelationSchema {
  name: string;
  /** Ordered key columns. A bare row variable coerces to the key (single key only). */
  keys: string[];
  /**
   * Ordered non-key columns. For a `decision` relation this is the single
   * solver-assigned value; for `data` it's the fixed columns (one for an applied
   * lookup like `demand.value`, several for an entity table like `battery`).
   */
  values: string[];
  kind: RelationKind;
  /**
   * Explicit foreign keys: column name → the relation it references. Needed only
   * when the column name differs from the referenced relation (e.g. `line.from`
   * references `bus`). A column whose name *is* a relation (`demand.bus`,
   * `gen_output.gen`) is an implicit foreign key — see {@link foreignKeys}.
   */
  refs?: Record<string, string>;
  /**
   * What a column holds, for the columns that aren't references — `float` where
   * nothing is said. A column that *is* a reference (see {@link foreignKeys})
   * takes its type from the relation it points at, and doesn't appear here:
   * `line.from` is a `bus`, whatever a bus id happens to be underneath.
   */
  types?: Record<string, ColumnType>;
  /**
   * A one-line, human-readable explanation of what the relation holds — shown
   * above its table in the DATA section. Optional; most relations are named
   * plainly enough (`demand`, `bus`) not to need one, but the AC admittance
   * terms (`Gff`, `Bft`, …) do.
   */
  description?: string;
}

export type Catalog = Record<string, RelationSchema>;

const rel = (
  name: string,
  keys: string[],
  values: string[],
  kind: RelationKind,
  refs?: Record<string, string>,
  types?: Record<string, ColumnType>,
): RelationSchema => ({ name, keys, values, kind, refs, types });

/**
 * The **data** relations of the multi-timestep grid model: the entity tables
 * (ranged over, read via `.attr`) and the applied lookups (`demand`, the
 * renewable-availability curves).
 * These are genuinely fixed — they're the shape of the grid the app models, and
 * `buildData` fills them from the topology and the curves.
 *
 * The *decision* relations are not here, nor the *derived* ones. A decision
 * relation is declared by the ranged rule that bounds it ({@link
 * decisionRelation}); a derived relation is defined by the rule that computes it
 * ({@link derivedRelation}). Both follow their rules' edits.
 */
export const GRID_DATA_CATALOG: Catalog = Object.fromEntries(
  [
    // Entity tables — data, ranged over, reached with `.attr`.
    // `bus.slack` marks the reference bus (theta rule skips it); `bus.in_balance`
    // marks buses that get a nodal-balance row (the balance rule filters on it,
    // matching the hand-written model's isolated-node skip).
    // A grid element's id is the name it carries in the topology ("bus5"), not a
    // number; the flags are 0/1. Everything unlisted is a float — MW, MWh, $/MWh.
    rel('bus', ['id'], ['slack', 'in_balance'], 'data', undefined, {
      id: 'string',
      slack: 'int',
      in_balance: 'int',
    }),
    // An hour index, which is why `t + 1` is arithmetic that means something.
    rel('timestep', ['t'], [], 'data', undefined, { t: 'int' }),
    // Period boundaries 0..N (one more than `timestep`); SOC is indexed on these.
    rel('soc_time', ['t'], [], 'data', undefined, { t: 'int' }),
    rel('gen', ['id'], ['min', 'max', 'cost', 'fuel'], 'data', undefined, {
      id: 'string',
      fuel: 'string',
    }),
    rel('line', ['id'], ['from', 'to', 'reactance', 'rating'], 'data', { from: 'bus', to: 'bus' }, {
      id: 'string',
    }),
    rel(
      'battery',
      ['id'],
      ['efficiency', 'max_charge', 'max_discharge', 'min_energy', 'max_energy', 'initial_soc'],
      'data',
      undefined,
      { id: 'string' },
    ),

    // Applied data relations — single value each.
    rel('demand', ['bus', 'timestep'], ['value'], 'data'),
    // Per-hour renewable availability, as MW available *to each unit* of that
    // fuel — the curve for the hour split evenly across the units. Per-unit
    // rather than total so a cap rule can read it straight off without needing
    // to count its siblings.
    rel('wind_strength', ['timestep'], ['value'], 'data'),
    rel('solar_strength', ['timestep'], ['value'], 'data'),
    // The single period boundary N, used to name the final state of charge.
    rel('horizon', ['t'], [], 'data', undefined, { t: 'int' }),
  ].map((r) => [r.name, r]),
);

// ---------------------------------------------------------------------------
// Decision relations, read off the rules that declare them
// ---------------------------------------------------------------------------

/** `name`, `name_2`, `name_3`… — the first form not already used. */
function distinct(name: string, taken: string[]): string {
  if (!taken.includes(name)) return name;
  for (let n = 2; ; n++) {
    if (!taken.includes(`${name}_${n}`)) return `${name}_${n}`;
  }
}

/**
 * The decision relation a ranged rule declares, e.g.
 *
 *   def soc_bounds(b: battery, t: soc_time):
 *     b.min_energy <= SOC(b, t) <= b.max_energy
 *
 * declares `SOC` keyed by (battery, soc_time) with one solver-assigned value.
 *
 * Each argument of the subject is one key position, named after the relation the
 * row variable ranges over — so adding a quantifier and indexing by it widens the
 * variable class, and every rule that applies it is then re-checked against the
 * new arity. An argument that isn't a bare row variable (a literal, arithmetic,
 * an unbound name) still occupies a position; it just gets a positional name and
 * no referenced relation, since there's nothing to read one off.
 *
 * Returns undefined for a rule that doesn't declare anything — a comparison
 * body, or a ranged body whose subject isn't an applied reference.
 */
export function decisionRelation(rule: Rule): RelationSchema | undefined {
  const body = rule.body;
  if (body.kind !== 'range' || body.expr.kind !== 'apply') return undefined;
  const subject = body.expr;

  const rangeOf = new Map(rule.quantifiers.map((q) => [q.var, q.relation]));
  const keys: string[] = [];
  const refs: Record<string, string> = {};

  subject.args.forEach((arg, index) => {
    const referenced = arg.value.kind === 'ref' ? rangeOf.get(arg.value.name) : undefined;
    const key = distinct(arg.name ?? referenced ?? `key${index + 1}`, keys);
    keys.push(key);
    if (referenced) refs[key] = referenced;
  });

  return { name: subject.relation, keys, values: ['value'], kind: 'decision', refs };
}

/**
 * The data relation a definition rule computes, e.g.
 *
 *   def gen_cap(g: gen, t: timestep):
 *     min(g.max, wind_strength(t)) if g.fuel == "wind" else g.max
 *
 * defines `gen_cap` keyed by (gen, timestep) with one value. Here the *whole
 * parameter list* is the key — there's no subject to read positions off, since
 * the rule's name is the relation and its body is the value.
 *
 * It's `data`, not `decision`: the rows are computed before the solve, from
 * relations that are themselves data.
 */
export function derivedRelation(rule: Rule): RelationSchema | undefined {
  if (rule.body.kind !== 'define') return undefined;

  const keys: string[] = [];
  const refs: Record<string, string> = {};
  for (const q of rule.quantifiers) {
    const key = distinct(q.relation, keys);
    keys.push(key);
    refs[key] = q.relation;
  }

  // The value starts out `unknown` rather than float: what a definition
  // computes is its body's business, and until something has typed that body
  // there is no answer — as opposed to a default one, which would be
  // indistinguishable from a definition that really does compute a float.
  return { name: rule.name, keys, values: ['value'], kind: 'data', refs, types: { value: 'unknown' } };
}

/**
 * The catalog a set of rules is checked and grounded against: the data relations
 * plus whatever decision relations the ranged rules among them declare.
 *
 * Derived from *every* parseable ranged rule, enabled or not: disabling a rule
 * takes its variables out of the model, but `SOC` still means what its rule says
 * it means, so the rules that reference it keep checking out.
 */
export function catalogFor(rules: Rule[], base: Catalog = GRID_DATA_CATALOG): Catalog {
  const catalog: Catalog = { ...base };
  for (const rule of rules) {
    const declared = decisionRelation(rule) ?? derivedRelation(rule);
    // The base half is fixed — the app fills those tables from the grid, and a
    // rule that took one of their names would replace the schema every other
    // rule is checked against. The rule set reports it (see ruleSet.ts).
    if (declared && !base[declared.name]) catalog[declared.name] = declared;
  }
  return catalog;
}

/** All columns of a relation, keys first. */
export function columnsOf(schema: RelationSchema): string[] {
  return [...schema.keys, ...schema.values];
}

/** Whether an applied reference to this relation is value-coercible (one non-key column). */
export function isValueCoercible(schema: RelationSchema): boolean {
  return schema.values.length === 1;
}

/**
 * The number of key columns — the arity a bare row of this relation spreads to
 * when used as an index. 1 for a single-key relation, more for a composite key.
 */
export function keyArity(schema: RelationSchema): number {
  return schema.keys.length;
}

/**
 * Effective foreign keys of a relation: column → referenced relation. Merges the
 * explicit {@link RelationSchema.refs} with the implicit rule that a column named
 * after a relation in the catalog references it (`demand.bus → bus`). This is
 * what lets composite-key spreading be checked by referenced relation, not just
 * arity: a `demand` row's keys resolve to (bus, timestep), so it can index a
 * relation whose keys resolve to (bus, timestep).
 */
export function foreignKeys(schema: RelationSchema, catalog: Catalog): Record<string, string> {
  const out: Record<string, string> = {};
  for (const col of columnsOf(schema)) {
    const explicit = schema.refs?.[col];
    if (explicit) out[col] = explicit;
    else if (catalog[col]) out[col] = col;
  }
  return out;
}
